import type {
  Form100Data,
  Form100CostOfSales,
  Form100SocialContributionsBreakdown,
  Form100TaxesAndDutiesBreakdown,
  Form10001Data,
  Form10002Data,
  Form10002LineGroups,
  Form10007Data,
  Period,
  TaxpayerMeta,
} from "../../types/api";
import {
  num,
  sumNums,
  provisionEntry,
  provisionTable,
  padRowNumber,
  type IsnaNumber,
} from "./helpers";

export interface BuildFno100Input {
  form100: Form100Data;
  form10001: Form10001Data;
  form10002: Form10002Data;
  form10007?: Form10007Data;
  meta: TaxpayerMeta;
  year: number;
  declarationDate: string;
}

function normalizeCostOfSales(cos: Form100CostOfSales | string): Form100CostOfSales {
  if (typeof cos === "object" && cos !== null) return cos;
  return {
    opening_inventory: "0",
    closing_inventory: "0",
    purchased_goods: "0",
    financial_services: "0",
    advertising_services: "0",
    consulting_services: "0",
    other_services: "0",
    labor_costs: "0",
    social_tax: "0",
    total_cost_of_sales: typeof cos === "string" ? cos : "0",
  };
}

function breakdownTotal(
  value: Form100SocialContributionsBreakdown | Form100TaxesAndDutiesBreakdown | string,
): string {
  return typeof value === "object" && value !== null ? value.total : value;
}

function nodGroupA() {
  return {
    total: null as IsnaNumber,
    manual: true,
    I: provisionEntry(),
    II: provisionEntry(),
    III: provisionEntry(),
    IV: provisionEntry(),
    V: provisionTable(),
  };
}

function citRatePercent(rate: string): IsnaNumber {
  const parsed = num(rate);
  return parsed === null ? null : Math.round(parsed * 100);
}

function groupColumn(line?: Form10002LineGroups) {
  return {
    I: num(line?.I),
    II: num(line?.II),
    III: num(line?.III),
    IV: num(line?.IV),
    total: line ? sumNums(line.I, line.II, line.III, line.IV) : null,
  };
}

function balanceColumn(form: Form10007Data | undefined, key: "opening" | "closing") {
  const assets = form?.assets;
  const liabilities = form?.liabilities;
  const equity = form?.equity;
  const value = (period?: Period) => num(period?.[key]);
  return {
    active: {
      _001: value(assets?.cash),
      _002: value(assets?.short_term_investments),
      _003: value(assets?.short_term_receivables),
      _004: value(assets?.inventory),
      _005: value(assets?.current_tax_assets),
      _006: value(assets?.assets_held_for_sale),
      _007: value(assets?.other_current_assets),
      _008: value(assets?.long_term_investments),
      _009: value(assets?.long_term_receivables),
      _010: value(assets?.equity_investments),
      _011: value(assets?.investment_property),
      _012: value(assets?.fixed_assets),
      _013: value(assets?.biological_assets),
      _014: value(assets?.exploration_assets),
      _015: value(assets?.intangible_assets),
      _016: value(assets?.deferred_tax_assets),
      _017: value(assets?.other_non_current),
      _018: value(assets?.total_assets),
    },
    commitment: {
      _019: value(liabilities?.short_term_financial),
      _020: value(liabilities?.tax_liabilities),
      _021: value(liabilities?.social_liabilities),
      _022: value(liabilities?.short_term_payables),
      _023: value(liabilities?.short_term_provisions),
      _024: value(liabilities?.other_short_term),
      _025: value(liabilities?.long_term_financial),
      _026: value(liabilities?.long_term_payables),
      _027: value(liabilities?.long_term_provisions),
      _028: value(liabilities?.deferred_tax_liabilities),
      _029: value(liabilities?.other_long_term),
      _030: value(liabilities?.total_liabilities),
    },
    capital: {
      _031: value(equity?.share_capital),
      _032: value(equity?.unpaid_capital),
      _033: value(equity?.share_premium),
      _034: value(equity?.treasury_shares),
      _035: value(equity?.reserves),
      _036: value(equity?.retained_earnings),
      _037: value(equity?.minority_interest),
      _038: value(equity?.total_equity),
    },
  };
}

function buildApplication01(form: Form10001Data) {
  return form.entries.map((entry, index) => ({
    A: padRowNumber(entry.row_number),
    B: entry.vendor_bin || null,
    C: entry.country_code || null,
    D: entry.foreign_tax_number || null,
    E: entry.expense_type_code != null ? String(entry.expense_type_code) : null,
    F: num(entry.amount),
    G: entry.sign != null ? String(entry.sign) : entry.expense_type_code != null ? "1" : null,
    index,
  }));
}

function buildApplication02(form: Form10002Data) {
  return {
    _001: groupColumn(form.line001),
    _002: groupColumn(form.line002),
    _003: groupColumn(form.line003),
    _004: groupColumn(form.line004),
    _005: groupColumn(form.line005),
    _006: groupColumn(form.line006),
    _007: groupColumn(form.line007),
    _008: groupColumn(form.line008),
    _009: groupColumn(form.line009),
    _010: groupColumn(form.line010),
    _011: groupColumn(form.line011),
    _012: num(form.line012),
  };
}

function buildApplication07(form?: Form10007Data) {
  return {
    atEndOfTaxPeriod: balanceColumn(form, "closing"),
    atBeginOfTaxPeriod: balanceColumn(form, "opening"),
  };
}

function emptyApplication08() {
  return {
    _001: null,
    _002: { A: null, B: null, C: null, D: null, total: null },
    _003: { A: null, B: null, C: null, D: null, E: null, F: null, total: null },
    _004: null,
  };
}

function emptyApplication10() {
  return {
    _001: null, _002: null, _003: null, _004: null, _005: null, _006: null,
    _007: null, _008: null, _009: null, _010: null, _011: null, _012: null, _013: null,
    _014: {
      I: null, II: null, III: null, IV: null, V: null, VI: null, VII: null, VIII: null,
      IX: null, X: null, XI: null, XII: null, XIII: null, XIV: null, XV: null, XVI: null,
      XVII: null, XVIII: null,
      XIX: { A: null, B: null, C: null, D: null, E: null, total: null },
      XX: null, XXI: null, total: null,
    },
    _015: {
      I: null, II: null, III: null, IV: null, V: null, VI: null, VII: null, VIII: null,
      IX: null, X: null, XI: null, total: null,
    },
    _016: null, _017: null, _018: null, _019: null, _020: null, _021: null,
    _022: null, _023: null, _024: null, _025: null, _026: null, _027: null,
  };
}

function emptyApplication12() {
  return {
    _001: null, _002: null, _003: null, _004: null, _005: null, _006: null, _007: null,
    _008: null, _009: null, _010: null, _011: null, _012: null, _013: null, _014: null,
  };
}

function emptyApplication13() {
  return {
    _001: null, _002: null, _003: null, _004: null, _005: null, _006: null,
    _007: null, _008: null, _009: null, _010: null, _011: null, _012: null,
  };
}

export function buildFno100(input: BuildFno100Input) {
  const { form100, form10001, form10002, form10007, meta, year, declarationDate } = input;
  const bin = form100.organization_bin;
  const name = meta.taxpayer_name_full;
  const type = meta.taxpayer_type;

  const income = form100.income;
  const deductions = form100.deductions;
  const tc = form100.tax_calculation;
  const cos = normalizeCostOfSales(deductions.cost_of_sales);

  const totalTaxableIncome = num(tc.total_taxable_income);
  const reductions = num(tc.reductions_art_288);
  const loss = totalTaxableIncome !== null && totalTaxableIncome < 0 ? -totalTaxableIncome : null;
  const afterReductions =
    totalTaxableIncome !== null && totalTaxableIncome > 0 ? totalTaxableIncome - (reductions ?? 0) : null;
  const totalDeductions = num(deductions.total_deductions);
  const nkoDeductions = num(deductions.nko_allocated_deductions);

  return {
    taxpayerCode: bin,
    periodType: "YEAR",
    periodValue: year,
    fnoYear: year,
    taxpayerNameRu: name,
    taxpayerNameEn: name,
    taxpayerNameKk: name,
    taxpayerNameQq: name,
    taxpayerType: type,
    creatorCode: bin,
    creatorType: type,
    creatorNameRu: name,
    creatorNameKk: name,
    creatorNameEn: name,
    creatorNameQq: name,
    declarationTypes: ["NEXT_PRIORITY"],
    ogdCodeByLocation: null,
    ogdCodeByResidence: null,
    noticeNumber: null,
    noticeDate: null,
    taxpayerCategories: [],
    fnoContent: {
      type: "fno100",
      taxpayerResponsibility: {
        fullNameOfHead: meta.chief_name,
        isResponsible: true,
        declarationDate,
        fullNameOfAuthorizedPerson: null,
        fullNameOfEmployeeWhoAcceptedDeclaration: null,
        declarationReceiptDate: null,
        declarationIncomingNumber: null,
        postmarkDate: null,
      },
      commonInfo: {
        selectedApplications: ["application_01", "application_02", "application_07"],
        _7: null,
        _10: "A",
        _11: { A: null, B: null },
        _12: false,
      },
      totalAnnualIncome: {
        _001: {
          I: num(income.interest_income),
          II: null,
          III: num(income.royalty_income),
          IV: num(income.rental_income),
          total: num(income.sales_revenue),
        },
        _002: num(income.asset_disposal_gain),
        _003: num(income.liability_write_off),
        _004: null,
        _005: null,
        _006: { I: null, total: null, manual: true },
        _007: { I: null, II: null, total: null },
        _008: null,
        _009: null,
        _010: num(income.grant_income),
        _011: null,
        _012: null,
        _013: null,
        _014: {
          I: provisionEntry(income.foreign_exchange_gain),
          II: provisionEntry(),
          III: provisionEntry(),
          IV: provisionEntry(),
          V: provisionTable(),
          total: num(income.other_income),
          manual: true,
        },
        _015: num(income.total_gross_income),
      },
      totalAnnualIncomeAdjustment: {
        _016: {
          I: provisionEntry(),
          II: provisionEntry(),
          III: provisionEntry(),
          IV: provisionEntry(),
          V: provisionTable(),
          total: num(form100.income_adjustments),
        },
        _017: null,
        _018: num(form100.adjusted_gross_income),
      },
      deduction: {
        _019: {
          I: num(cos.opening_inventory),
          II: num(cos.closing_inventory),
          III: {
            A: num(cos.purchased_goods),
            B: num(cos.financial_services),
            C: num(cos.advertising_services),
            D: num(cos.consulting_services),
            E: null,
            F: null,
            G: null,
            H: num(cos.other_services),
            total: sumNums(
              cos.purchased_goods,
              cos.financial_services,
              cos.advertising_services,
              cos.consulting_services,
              cos.other_services,
            ),
          },
          IV: num(cos.labor_costs),
          V: null,
          VI: null,
          VII: null,
          VIII: null,
          IX: null,
          total: num(cos.total_cost_of_sales),
        },
        _020: num(deductions.penalties_and_fines),
        _021: null,
        _022: num(breakdownTotal(deductions.social_contributions)),
        _023: num(deductions.interest_expense),
        _024: null,
        _025: null,
        _026: { total: num(deductions.fixed_asset_deductions), manual: true },
        _027: null,
        _028: { I: null, II: null, III: null, IV: null, total: null },
        _029: null,
        _030: null,
        _031: null,
        _032: null,
        _033: null,
        _034: null,
        _035: null,
        _036: num(breakdownTotal(deductions.taxes_and_duties)),
        _037: null,
        _038: null,
        _039: num(deductions.other_expenses),
        _040: {
          I: totalDeductions,
          II: nkoDeductions,
          III: null,
          IV: null,
          total: nkoDeductions ?? totalDeductions,
        },
      },
      incomeDeductionAdjustment: {
        _041: { I: null, II: null, total: null },
        _042: null,
        _043: null,
      },
      calculationNodAndNO: {
        calculationNod: {
          _044: num(tc.taxable_income),
          _045: num(tc.foreign_source_income),
          _046: { I: null, II: null, total: num(tc.exempt_income) },
          _047: totalTaxableIncome,
          _048: { I: null, II: null, total: null },
          _049: null,
          _050: null,
          _051: { total: loss, A: nodGroupA() },
          _052: { total: reductions, A: nodGroupA(), B: nodGroupA() },
          _053: afterReductions === 0 ? null : afterReductions,
          _054: { total: num(tc.loss_carryforward), I: null, autoTotal_app6: false },
          _055: num(tc.adjusted_taxable_income),
        },
        calculationNO: {
          _056: citRatePercent(tc.cit_rate),
          _057: num(tc.cit_calculated),
          _058: {
            I: num(tc.foreign_tax_credit),
            II: null,
            III: null,
            IV: null,
            V: null,
            VI: null,
            total: num(tc.cit_payable),
          },
          _059: { total: null, A: nodGroupA() },
          _060: null,
          _061: { I: null, II: { A: null, B: null }, III: null, IV: null },
          _062: num(tc.total_cit),
          _063: null,
          _064: null,
          _065: null,
        },
      },
      application_01: buildApplication01(form10001),
      application_02: buildApplication02(form10002),
      application_03: {
        expense: [],
        furtherInfo: {
          K1_1: null,
          K2_1: null,
          _1: null,
          _2: null,
          _3: null,
          _4: { from: null, to: null },
        },
      },
      application_04: [],
      application_05: [],
      application_06: [],
      application_07: buildApplication07(form10007),
      application_08: emptyApplication08(),
      application_09: [],
      application_10: emptyApplication10(),
      application_11: [],
      application_12: emptyApplication12(),
      application_13: emptyApplication13(),
    },
    currencyCode: "KZT",
  };
}
